// Shared helpers for the Direct License Certificate feature: form value
// initialisation, persistence, validation and date formatting.

export const STORAGE_KEY = "bodhistreams.certificate.form";

export const formatInputDate = (value, fallback = new Date()) => {
	if (!value) {
		return fallback.toISOString().slice(0, 10);
	}

	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) {
		return fallback.toISOString().slice(0, 10);
	}

	return parsed.toISOString().slice(0, 10);
};

export const formatCertificateDate = (value) => {
	if (!value) {
		return "................";
	}

	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) {
		return value;
	}

	return parsed.toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
};

export const getSavedValues = () => {
	if (typeof window === "undefined") {
		return {};
	}

	try {
		const saved = window.localStorage.getItem(STORAGE_KEY);
		return saved ? JSON.parse(saved) : {};
	} catch {
		return {};
	}
};

export const getSubscriberName = (user) => {
	const fullName = [user?.first_name, user?.last_name]
		.filter(Boolean)
		.join(" ");
	return fullName || user?.email || "Bodhistreams subscriber";
};

export const sanitizeFileName = (value) => {
	if (!value) {
		return "bodhistreams-certificate";
	}

	return value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-|-$/g, "");
};

// Fields the visitor fills in themselves. Everything else on the certificate
// is derived from the account, so it is neither editable nor persisted.
const EDITABLE_FIELDS = ["business_name", "property_address", "contact_name"];

export const buildInitialValues = (user) => {
	const savedValues = getSavedValues();
	const subscriberName = getSubscriberName(user);

	return {
		business_name: savedValues.business_name || subscriberName,
		property_address: savedValues.property_address || "",
		contact_name: savedValues.contact_name || subscriberName,
		// Read straight from the account so the certificate always matches the
		// live membership rather than a stale browser copy. The start date is
		// when the current paid membership was granted, which covers both a
		// Stripe checkout and a manual admin assignment, and is left blank
		// rather than guessed when the account carries no date.
		contact_email: user?.email || "",
		service_start_date: user?.membership_assigned_at
			? formatInputDate(user.membership_assigned_at)
			: "",
	};
};

export const saveValues = (values) => {
	if (typeof window === "undefined") {
		return;
	}

	const editable = Object.fromEntries(
		EDITABLE_FIELDS.map((field) => [field, values[field]]),
	);
	window.localStorage.setItem(STORAGE_KEY, JSON.stringify(editable));
};

export const validateValues = (values) => {
	const errors = {};
	if (!values.business_name?.trim()) {
		errors.business_name = "Enter the business name";
	}
	if (!values.property_address?.trim()) {
		errors.property_address = "Enter the property address";
	}
	if (!values.contact_name?.trim()) {
		errors.contact_name = "Enter the authorized contact";
	}
	return errors;
};
